import { errorMsg } from '../../utils/errorMsg';
import React, { useState, useEffect } from 'react';
import {
  Container, Grid, Card, CardContent, Typography, CircularProgress, Alert,
  Button, List, ListItem, ListItemText, Chip, Box,
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import GroupsIcon from '@mui/icons-material/Groups';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { Doughnut } from 'react-chartjs-2';
import { useNavigate } from 'react-router-dom';
import { dashboardGCs, listarGCs, aprovarGC, adicionarMembroGC } from '../../api/gruposCrescimento';
import { membrosSemGC } from '../../api/gruposCrescimento';
import ModuleHeader from '../../components/ModuleHeader';
import { registerCharts, doughnutOptions, CHART } from '../../components/ChartTheme';

registerCharts();

function GCDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [pendentes, setPendentes] = useState([]);
  const [semGc, setSemGc] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [aprovandoId, setAprovandoId] = useState(null);
  const [addDialog, setAddDialog] = useState({ open: false, membro: null, gcSelecionado: '' });
  const [gcsAtivos, setGcsAtivos] = useState([]);

  const carregar = async () => {
    setLoading(true);
    setErro('');
    try {
      const [statsData, gcsData, semGcData, gcsAtivosData] = await Promise.all([
        dashboardGCs(),
        listarGCs({ aprovado: false }),
        membrosSemGC(),
        listarGCs({ status: 'ativo', aprovado: true })
      ]);
      setStats(statsData);
      setPendentes(gcsData);
      setSemGc(semGcData);
      setGcsAtivos(gcsAtivosData);
    } catch (err) {
      setErro(errorMsg(err) || 'Erro ao carregar dashboard');
    }
    setLoading(false);
  };

  useEffect(() => { carregar(); }, []);

  const abrirAddDialog = (membro) => {
    setAddDialog({ open: true, membro, gcSelecionado: '' });
  };

  const confirmarAddMembro = async () => {
    if (!addDialog.gcSelecionado) return;
    try {
      await adicionarMembroGC(addDialog.gcSelecionado, addDialog.membro.id);
      setAddDialog({ open: false, membro: null, gcSelecionado: '' });
      carregar();
    } catch (err) {
      alert(errorMsg(err) || 'Erro ao adicionar membro');
    }
  };

  const handleAprovar = async (id) => {
    setAprovandoId(id);
    try {
      await aprovarGC(id);
      carregar();
    } catch (err) {
      alert(errorMsg(err) || 'Erro ao aprovar GC');
    }
    setAprovandoId(null);
  };

  if (loading) return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, md: 2 }, textAlign: 'center' }}>
      <CircularProgress />
    </Container>
  );
  if (erro) return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, md: 2 } }}>
      <Alert severity="error">{erro}</Alert>
    </Container>
  );
  if (!stats) return null;

  const kpis = [
    { icon: <GroupsIcon />, color: 'primary.main', value: stats.total_gcs || 0, label: 'Total de GCs' },
    { icon: <CheckCircleIcon />, color: 'secondary.main', value: stats.gcs_ativos || 0, label: 'GCs Ativos' },
    { icon: <ScheduleIcon />, color: 'primary.main', value: stats.gcs_pendentes_aprovacao || 0, label: 'Pendentes Aprovação' },
    { icon: <PersonAddIcon />, color: 'secondary.main', value: stats.membros_em_gc || 0, label: 'Membros em GCs' },
  ];

  const doughnutData = {
    labels: ['Ativos', 'Inativos'],
    datasets: [{
      data: [stats.gcs_ativos || 0, (stats.total_gcs || 0) - (stats.gcs_ativos || 0)],
      backgroundColor: [CHART.teal, CHART.lime],
      borderWidth: 0,
      hoverOffset: 6,
    }],
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, md: 2 } }}>
      <ModuleHeader module="Grupos de Crescimento" title="Dashboard de Grupos" />

      <Grid container spacing={2} sx={{ mb: 4 }}>
        {kpis.map((k, i) => (
          <Grid item xs={6} sm={4} md={3} key={i}>
            <Card sx={{ borderRadius: 1 }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Box sx={{ color: k.color, display: 'flex' }}>{k.icon}</Box>
                  <Typography variant="caption" color="text.secondary">{k.label}</Typography>
                </Box>
                <Typography sx={{ fontWeight: 800, fontSize: '4.4rem', color: k.color, lineHeight: 1 }}>{k.value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 1, height: '100%' }}><CardContent>
            <Typography sx={{ fontWeight: 700, mb: 2 }}>Status dos GCs</Typography>
            <Box sx={{ height: 220 }}>
              <Doughnut data={doughnutData} options={doughnutOptions()} />
            </Box>
          </CardContent></Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 1, height: '100%' }}><CardContent>
            <Typography sx={{ fontWeight: 700, mb: 2 }}>Pendentes de Aprovação</Typography>
            {pendentes.length === 0 ? (
              <Typography variant="body2" color="text.secondary">Nenhum GC pendente.</Typography>
            ) : (
              <List dense sx={{ maxHeight: 320, overflow: 'auto' }}>
                {pendentes.map(gc => (
                  <ListItem key={gc.id} disablePadding sx={{ mb: 1 }}>
                    <ListItemText
                      primary={gc.nome}
                      secondary={gc.lider_nome || ''}
                      primaryTypographyProps={{ sx: { cursor: 'pointer', fontWeight: 600 } }}
                      onClick={() => navigate(`/grupos-crescimento/${gc.id}`)}
                    />
                    <Button
                      size="small"
                      color="success"
                      startIcon={<CheckCircleIcon />}
                      onClick={() => handleAprovar(gc.id)}
                      disabled={aprovandoId === gc.id}
                    >
                      {aprovandoId === gc.id ? <CircularProgress size={14} /> : 'Aprovar'}
                    </Button>
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent></Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 1, height: '100%' }}><CardContent>
            <Typography sx={{ fontWeight: 700, mb: 2 }}>Membros sem GC</Typography>
            {semGc.length === 0 ? (
              <Typography variant="body2" color="text.secondary">Todos os membros estão alocados.</Typography>
            ) : (
              <List dense sx={{ maxHeight: 320, overflow: 'auto' }}>
                {semGc.map(m => (
                  <ListItem key={m.id} disablePadding sx={{ mb: 1 }}>
                    <ListItemText
                      primary={m.nome}
                      secondary={`${m.email || ''} ${m.telefone ? `— ${m.telefone}` : ''}`}
                      primaryTypographyProps={{ sx: { fontWeight: 600 } }}
                    />
                    <Chip label="Adicionar" size="small" color="primary" variant="outlined" sx={{ cursor: 'pointer' }}
                      icon={<PersonAddIcon fontSize="small" />}
                      onClick={() => abrirAddDialog(m)}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent></Card>
        </Grid>
      </Grid>

      <Dialog open={addDialog.open} onClose={() => setAddDialog({ open: false, membro: null, gcSelecionado: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>Adicionar {addDialog.membro?.nome} a um GC</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Selecione o GC</InputLabel>
            <Select
              value={addDialog.gcSelecionado}
              label="Selecione o GC"
              onChange={e => setAddDialog(d => ({ ...d, gcSelecionado: e.target.value }))}
            >
              <MenuItem value="">Selecione...</MenuItem>
              {gcsAtivos.map(gc => (
                <MenuItem key={gc.id} value={gc.id}>{gc.nome} — {gc.lider_nome || 'N/A'}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialog({ open: false, membro: null, gcSelecionado: '' })}>Cancelar</Button>
          <Button variant="contained" onClick={confirmarAddMembro} disabled={!addDialog.gcSelecionado}>
            Adicionar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default GCDashboard;
